// Слот: экран игры целиком (барабаны, линии, бонуска с колесом, крупные выигрыши, таблица выплат, звук, авто/турбо)
// и весь протокол сокета slot:* (docs/frontend/frontend_slots.md). Дизайн и анимации — из прототипа prototypes/slots.
//
// Фронт ничего не считает: результат спина целиком присылает сервер (slot:spin:ok), здесь только показ.
// Подключение — docs/frontend/frontend_slots_kit.md:
//
//   import { createSlotGame } from './slot-game.js';
//   import './slot.css';
//   const game = createSlotGame(el, { socket, t, locale, sound, onBalanceChange, onDepositRequired, onLoginRequired });
//   ...
//   game.destroy(); // уход с экрана: закрывает сессию (slot:close), отписывается, убирает DOM и слушатели

import { createSound } from './slot-sound.js';
import { createFx } from './slot-fx.js';
import { createHaptic } from './slot-haptic.js';
import RU from './slot-strings.ru.js';

const SPIN_TIMEOUT_MS = 3000;   // нет ни slot:spin:ok, ни user:alert за это время — спин не сыгран (гайд, «Разрывы»)
const OPEN_TIMEOUT_MS = 5000;
const PREFS_KEY = 'slt_prefs';
const NS = 'http://www.w3.org/2000/svg';

// Внешний вид символов по ключу из slot:config.symbols. ︎ — чтобы масти не превращались в эмодзи.
// Заменить на картинки — опция symbolView (см. гайд, «Свои картинки символов»).
const VIEW = {
  L1: { cls: 'slt-low slt-s0', html: '♠︎' },
  L2: { cls: 'slt-low slt-s1', html: '♣︎' },
  L3: { cls: 'slt-low slt-s2', html: '♦︎' },
  L4: { cls: 'slt-low slt-s3', html: '♥︎' },
  H1: { cls: 'slt-high slt-h1', html: '🎯' },
  H2: { cls: 'slt-high slt-h2', html: '💣' },
  H3: { cls: 'slt-high slt-h3', html: '🗡️' },
  H4: { cls: 'slt-high slt-h4', html: '👑' },
  W: { cls: 'slt-wild', html: '<span>WILD</span>' },
  S: { cls: 'slt-scatter', html: '<span class="slt-gem">💎</span><span class="slt-tag">BONUS</span>' },
};

const sleep = ms => new Promise(r => setTimeout(r, ms));
const interp = (s, p) => String(s).replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (p && p[k] != null ? String(p[k]) : m));
const newId = () => 'c' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);

// Ставки на линию: «круглые» 1-2-5 × 10ⁿ в пределах лимита полной ставки + точный минимум для этого числа линий
function betSteps(bet, lines) {
  const lo = Math.ceil(bet.min / lines), hi = Math.floor(bet.max / lines);
  if (lo < 1 || lo > hi) return [];
  const out = new Set([lo]);
  for (let p = 1; p <= hi; p *= 10) for (const k of [1, 2, 5]) { const v = k * p; if (v >= lo && v <= hi) out.add(v); }
  return [...out].sort((a, b) => a - b);
}

export function createSlotGame(root, opts = {}) {
  const socket = opts.socket;
  if (!root || !socket) throw new Error('createSlotGame: нужны элемент и opts.socket');

  // ---------- тексты и числа ----------
  const t = (key, p) => {
    const s = typeof opts.t === 'function' ? opts.t(key, p) : null;
    return typeof s === 'string' && s ? s : interp(RU[key] != null ? RU[key] : key, p);
  };
  const locale = opts.locale || 'ru-RU';
  const fmt = n => Math.round(n).toLocaleString(locale);
  const fmtX = x => { const d = x >= 100 ? 0 : x >= 10 ? 1 : 2; return x.toLocaleString(locale, { minimumFractionDigits: d, maximumFractionDigits: d }); };
  const view = key => (typeof opts.symbolView === 'function' && opts.symbolView(key)) || VIEW[key] || { cls: '', html: '?' };

  // ---------- состояние ----------
  const prefs = (() => { try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; } catch (e) { return {}; } })();
  const st = {
    config: null, keyOf: [], W: -1, S: -1, pools: null,
    connected: !!socket.connected, ready: false, disabled: false, minSpinMs: 300, lastSpinAt: 0,
    main: null, wallet: 0, session: null, lastSeq: -1,
    pendingCid: null, pendingBet: 0, unrevealed: 0,   // для баланса на экране: ставка в полёте и ещё не показанный выигрыш
    lines: prefs.lines | 0, bpl: prefs.bpl | 0 || null,
    busy: false, auto: false, turbo: !!prefs.turbo, sound: opts.sound !== false, skip: null, dead: false,
    lastShown: null,
  };
  const savePrefs = () => { try { localStorage.setItem(PREFS_KEY, JSON.stringify({ lines: st.lines, bpl: st.bpl, turbo: st.turbo })); } catch (e) { /* не критично */ } };
  const timing = () => (st.turbo
    ? { first: 230, step: 60, antic: 750, cell: 20, win: 320, pause: 90 }
    : { first: 620, step: 190, antic: 1500, cell: 30, win: 950, pause: 260 });

  // ---------- разметка ----------
  root.classList.add('slt');
  if (opts.dir) root.setAttribute('dir', opts.dir);
  root.innerHTML = `
<div class="slt-app">
  <header class="slt-top">
    <div class="slt-balance"><span data-r="balLabel"></span><b data-r="balance">—</b> 💎</div>
    <div class="slt-top-btns"><button type="button" data-r="btnPay"></button><button type="button" data-r="btnSound"></button></div>
  </header>
  <div class="slt-notice slt-hidden" data-r="notice"></div>
  <div class="slt-fsbar slt-hidden" data-r="fsbar">
    <div class="slt-fs-count"><span data-r="fsLabel"></span> <b data-r="fsNow">1</b> / <span data-r="fsAll">0</span></div>
    <div class="slt-ladder" data-r="ladder"></div>
    <div class="slt-fs-total"><span data-r="fsTotalLabel"></span> <b data-r="fsTotal">0</b> 💎</div>
  </div>
  <div class="slt-stage" data-r="stage" dir="ltr">
    <div class="slt-reels" data-r="reels"></div>
    <svg class="slt-lines" data-r="lines" viewBox="0 0 500 300" preserveAspectRatio="none"></svg>
    <div class="slt-overlay slt-hidden" data-r="overlay"></div>
  </div>
  <div class="slt-winbar" data-r="winbar"></div>
  <div class="slt-spinid" data-r="spinid"></div>
  <footer class="slt-controls" data-r="controls">
    <div class="slt-ctl"><label data-r="lblBet"></label><div class="slt-stepper"><button type="button" data-act="bet-">−</button><b data-r="lineBet">—</b><button type="button" data-act="bet+">+</button></div></div>
    <div class="slt-ctl"><label data-r="lblLines"></label><div class="slt-stepper"><button type="button" data-act="lines-">−</button><b data-r="nLines">—</b><button type="button" data-act="lines+">+</button></div></div>
    <div class="slt-ctl slt-total"><label data-r="lblTotal"></label><b><span data-r="totalBet">—</span> 💎</b></div>
    <button type="button" class="slt-btn-spin" data-r="btnSpin"></button>
    <div class="slt-side"><button type="button" data-r="btnAuto"></button><button type="button" data-r="btnTurbo"></button></div>
  </footer>
</div>
<canvas class="slt-fx" data-r="fx"></canvas>
<div class="slt-modal slt-hidden" data-r="modal"></div>`;
  const $ = {};
  root.querySelectorAll('[data-r]').forEach(el => { $[el.dataset.r] = el; });
  const stage = $.stage, winbar = $.winbar;

  $.balLabel.textContent = t('balance');
  $.btnPay.textContent = t('paytable_btn');
  $.fsLabel.textContent = t('fs_counter');
  $.fsTotalLabel.textContent = t('fs_total');
  $.lblBet.textContent = t('bet_per_line');
  $.lblLines.textContent = t('lines');
  $.lblTotal.textContent = t('total_bet');
  $.btnAuto.textContent = t('auto');
  $.btnTurbo.textContent = t('turbo');
  winbar.textContent = t('loading');

  const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
  const sfx = createSound(() => st.sound && !st.dead);
  const fx = createFx($.fx);
  const hx = createHaptic(opts.haptic, () => st.auto || st.turbo, () => !st.dead);

  // ---------- плашка уведомлений ----------
  let noticeTag = '';
  function notice(text, kind, tag) {
    $.notice.textContent = text;
    $.notice.classList.toggle('slt-info', kind === 'info');
    $.notice.classList.remove('slt-hidden');
    noticeTag = tag || '';
  }
  function hideNotice(tag) {
    if (tag && noticeTag !== tag) return;
    $.notice.classList.add('slt-hidden');
    noticeTag = '';
  }

  // ---------- ожидание ответов сервера ----------
  const waiters = new Set();
  function waitEvent(test, ms) {
    return new Promise(res => {
      const w = { test, res };
      w.timer = setTimeout(() => { waiters.delete(w); res(null); }, ms);
      waiters.add(w);
    });
  }
  function feed(type, data) {
    let used = false;
    for (const w of [...waiters]) {
      if (w.test(type, data)) { clearTimeout(w.timer); waiters.delete(w); w.res({ type, data }); used = true; }
    }
    return used;
  }

  // ---------- баланс на экране = основной + резерв − ставка в полёте − ещё не показанный выигрыш ----------
  let balRaf = 0;
  function renderBalance() {
    balRaf = 0;
    if (st.dead) return;
    const known = st.main !== null || st.session;
    const shown = known ? Math.max(0, (st.main || 0) + st.wallet - st.pendingBet - st.unrevealed) : null;
    $.balance.textContent = shown === null ? '—' : fmt(shown);
    if (shown !== st.lastShown) {
      st.lastShown = shown;
      if (typeof opts.onBalanceChange === 'function') opts.onBalanceChange({ main: st.main, wallet: st.wallet, shown });
    }
  }
  const scheduleBalance = () => { if (!balRaf) balRaf = requestAnimationFrame(renderBalance); };
  function reveal(n) { st.unrevealed = Math.max(0, st.unrevealed - n); scheduleBalance(); }

  // ---------- барабаны ----------
  const reels = []; // { el, strip, cells: [три видимые клетки], pre: анимация до ответа сервера }
  let ro = null;

  function cell(sym) {
    const d = el('div', 'slt-cell');
    const v = view(st.keyOf[sym]);
    d.innerHTML = '<div class="slt-sym ' + v.cls + '">' + v.html + '</div>';
    return d;
  }
  // Случайный символ для «прокрутки» (что видно в движении, кроме финальных трёх клеток). Скаттер не подмешиваем,
  // wild — только на барабанах 2–4, как на настоящих лентах.
  function randSym(r) { const p = st.pools[r]; return p[Math.floor(Math.random() * p.length)]; }
  function buildPools() {
    const ids = st.config.symbols.map(s => s.id);
    st.pools = [0, 1, 2, 3, 4].map(r => ids.filter(id => id !== st.S && !(id === st.W && (r === 0 || r === 4))));
  }
  // Лента в покое: одна запасная клетка сверху (для отскока) и три видимые
  function setStatic(R) {
    R.strip.style.height = (4 / 3 * 100) + '%';
    R.strip.style.transform = 'translateY(-25%)';
  }
  function initReels() {
    $.reels.replaceChildren();
    reels.length = 0;
    for (let r = 0; r < 5; r++) {
      const reelEl = el('div', 'slt-reel'), strip = el('div', 'slt-strip');
      const cells = [randSym(r), randSym(r), randSym(r)].map(cell);
      strip.appendChild(cell(randSym(r)));
      cells.forEach(c => strip.appendChild(c));
      reelEl.appendChild(strip); $.reels.appendChild(reelEl);
      const R = { el: reelEl, strip, cells, pre: null };
      setStatic(R); reels.push(R);
    }
    const fit = () => root.style.setProperty('--cell', (reels[0].el.clientWidth || 100) + 'px');
    ro = new ResizeObserver(fit); ro.observe(stage); fit();
  }

  // Пока ждём ответ сервера (обычно 50–300 мс сети) — барабаны уже крутятся по кругу, чтобы нажатие отзывалось сразу
  function preSpin() {
    const T = timing();
    reels.forEach((R, r) => {
      const M = 8, syms = [];
      for (let i = 0; i < M; i++) syms.push(randSym(r));
      const frag = document.createDocumentFragment();
      syms.concat(syms).forEach(s => frag.appendChild(cell(s)));
      R.strip.replaceChildren(frag);
      R.strip.style.height = (2 * M / 3 * 100) + '%';
      R.el.classList.add('slt-blur');
      R.pre = R.strip.animate([{ transform: 'translateY(-50%)' }, { transform: 'translateY(0)' }], { duration: M * T.cell * 1.2, iterations: Infinity });
    });
  }
  // Сервер не ответил или отказал — возвращаем прежние символы
  function cancelPreSpin() {
    reels.forEach((R, r) => {
      if (!R.pre) return;
      R.pre.cancel(); R.pre = null;
      R.strip.replaceChildren(cell(randSym(r)), ...R.cells);
      R.el.classList.remove('slt-blur');
      setStatic(R);
    });
  }

  // Прокрутка до присланного сервером поля grid[барабан][ряд]. При двух скаттерах следующие барабаны крутятся
  // дольше и подсвечиваются (ожидание третьего).
  function spinReels(grid, mode) {
    const T = timing();
    const anims = [], timers = [], all = [];
    let t = 0, seen = 0, landed = 0, skipped = false;

    for (let r = 0; r < 5; r++) {
      const antic = mode === 'base' && seen >= 2;
      t += r === 0 ? T.first : (antic ? T.antic : T.step);
      const R = reels[r];
      const K = Math.round(t / T.cell);

      const frag = document.createDocumentFragment(), fin = [];
      frag.appendChild(cell(randSym(r)));
      for (let i = 0; i < 3; i++) { const c = cell(grid[r][i]); fin.push(c); frag.appendChild(c); }
      for (let i = 0; i < K; i++) frag.appendChild(cell(randSym(r)));
      if (R.pre) {
        R.pre.cancel(); R.pre = null;
        for (let i = 0; i < 3; i++) frag.appendChild(cell(randSym(r)));
      } else {
        R.cells.forEach(c => { c.className = 'slt-cell'; frag.appendChild(c); });
      }
      R.strip.replaceChildren(frag);

      const n = K + 7;
      R.strip.style.height = (n / 3 * 100) + '%';
      const at = v => 'translateY(' + (-v / n * 100).toFixed(4) + '%)';
      const a = R.strip.animate([
        { transform: at(n - 3), easing: 'cubic-bezier(.3,.3,.45,1)' },
        { transform: at(0.72), offset: 0.9, easing: 'ease-in-out' },
        { transform: at(1) },
      ], { duration: t, fill: 'forwards' });
      R.el.classList.add('slt-blur');
      timers.push(setTimeout(() => R.el.classList.remove('slt-blur'), t * 0.8));

      const scRow = mode === 'base' ? grid[r].indexOf(st.S) : -1;
      const anticMs = T.antic;
      all.push(a.finished.then(() => {
        R.el.classList.remove('slt-blur', 'slt-antic');
        while (R.strip.children.length > 4) R.strip.lastChild.remove();
        setStatic(R); a.cancel();
        R.cells = fin;
        sfx.stop();
        hx.stop();
        if (scRow >= 0) {
          landed++;
          fin[scRow].classList.add('slt-land');
          sfx.scatter(landed);
          hx.scatter(landed);
          if (!skipped) fx.burst(...fx.center(fin[scRow]), 14, 5);
        } else if (r === 4) hx.reelsDone();
        if (mode === 'base' && landed >= 2 && r < 4 && !skipped) {
          reels[r + 1].el.classList.add('slt-antic');
          sfx.antic(anticMs);
          hx.antic(anticMs);
        }
      }, () => { /* анимацию отменили (destroy) */ }));
      if (scRow >= 0) seen++;
      anims.push(a);
    }

    st.skip = () => { skipped = true; hx.stop(); anims.forEach(a => { try { a.finish(); } catch (e) { /* уже остановлена */ } }); };
    return Promise.all(all).then(() => { st.skip = null; timers.forEach(clearTimeout); });
  }

  // ---------- показ выигрыша ----------
  let previewTimer = 0;
  function clearWins() {
    clearTimeout(previewTimer);
    stage.classList.remove('slt-has-win');
    $.lines.classList.remove('slt-out');
    $.lines.replaceChildren();
    reels.forEach(R => R.cells.forEach(c => c.classList.remove('slt-win', 'slt-party')));
  }

  const lineColor = l => 'hsl(' + (l * 47 % 360) + ' 95% 62%)';

  // Показ активных линий поверх барабанов при смене их числа. Линии чуть разведены по высоте, чтобы совпадающие
  // участки не сливались; последняя активная выделена и подписана номером. Через пару секунд гаснет.
  function previewLines(focus) {
    if (!st.config || !reels.length) return;
    clearWins();
    const PL = st.config.paylines, svg = $.lines;
    const shift = l => ((l % 5) - 2) * 7;
    const draw = l => {
      const pl = document.createElementNS(NS, 'polyline'), dy = shift(l);
      const pts = PL[l].map((row, r) => (r * 100 + 50) + ',' + (row * 100 + 50 + dy));
      pts.unshift('4,' + (PL[l][0] * 100 + 50 + dy));
      pts.push('496,' + (PL[l][4] * 100 + 50 + dy));
      pl.setAttribute('points', pts.join(' '));
      pl.setAttribute('class', l === focus ? 'slt-pv slt-new' : 'slt-pv');
      pl.style.stroke = pl.style.color = lineColor(l);
      svg.appendChild(pl);
    };
    for (let l = 0; l < st.lines; l++) if (l !== focus) draw(l);
    draw(focus);

    const y = PL[focus][0] * 100 + 50 + shift(focus);
    const g = document.createElementNS(NS, 'g');
    g.setAttribute('class', 'slt-pv-tag');
    const c = document.createElementNS(NS, 'circle');
    c.setAttribute('cx', '22'); c.setAttribute('cy', String(y)); c.setAttribute('r', '17');
    const tx = document.createElementNS(NS, 'text');
    tx.setAttribute('x', '22'); tx.setAttribute('y', String(y + 6)); tx.setAttribute('text-anchor', 'middle');
    tx.textContent = String(focus + 1);
    g.append(c, tx);
    g.style.fill = lineColor(focus);
    svg.appendChild(g);

    winbar.classList.remove('slt-hit');
    winbar.textContent = t('lines_active', { n: st.lines, max: PL.length });
    previewTimer = setTimeout(() => {
      svg.classList.add('slt-out');
      previewTimer = setTimeout(clearWins, 450);
    }, 2200);
  }

  function countUp(node, from, to, ms) {
    return new Promise(done => {
      const t0 = performance.now();
      (function tick(now) {
        const k = Math.min(1, (now - t0) / ms), e = 1 - Math.pow(1 - k, 3);
        node.textContent = fmt(from + (to - from) * e);
        if (k < 1 && !st.dead) requestAnimationFrame(tick); else done();
      })(t0);
    });
  }

  // Оверлей поверх поля; закрывается по нажатию, по кнопке «Крутить» (через st.skip) или по таймеру
  function overlay(nodes, ms) {
    const ov = $.overlay;
    ov.replaceChildren(...nodes, el('div', 'slt-ov-hint', t('tap_to_continue')));
    ov.classList.remove('slt-hidden');
    return new Promise(res => {
      let done = false, timer = 0;
      const fin = () => {
        if (done) return; done = true;
        clearTimeout(timer);
        ov.classList.add('slt-hidden'); ov.replaceChildren(); ov.onclick = null; st.skip = null; res();
      };
      ov.onclick = fin; st.skip = fin; timer = setTimeout(fin, ms);
    });
  }

  async function bigWin(amount, x) {
    const title = x >= 100 ? t('epic_win') : x >= 40 ? t('mega_win') : t('big_win');
    sfx.big();
    hx.big(x >= 100 ? 3 : x >= 40 ? 2 : 1, st.turbo ? 900 : 2000);
    fx.rain(x >= 100 ? 220 : x >= 40 ? 140 : 80);
    fx.burst(...fx.center(stage), 60, 13);
    const num = el('span', null, '0');
    const amt = el('div', 'slt-ov-amount'); amt.append(num, ' 💎');
    const p = overlay([el('div', 'slt-ov-title', title), amt, el('div', 'slt-ov-sub', t('x_of_bet', { x: fmtX(x) }))], st.turbo ? 1800 : 3600);
    countUp(num, 0, amount, st.turbo ? 900 : 2000);
    await p;
    hx.stop();
  }

  // wins / scatter — из ответа сервера; amount — уже посчитанная сервером сумма (во фриспине — с множителем)
  async function showWins(wins, scatter, mult, bet, amount) {
    const T = timing(), svg = $.lines, PL = st.config.paylines;
    stage.classList.add('slt-has-win');
    for (const w of wins) {
      for (const [r, row] of w.cells) reels[r].cells[row].classList.add('slt-win');
      const pl = document.createElementNS(NS, 'polyline');
      pl.setAttribute('points', PL[w.line].map((row, r) => (r * 100 + 50) + ',' + (row * 100 + 50)).join(' '));
      pl.style.stroke = pl.style.color = lineColor(w.line);
      svg.appendChild(pl);
    }
    if (scatter) scatter.cells.forEach(([r, row]) => reels[r].cells[row].classList.add('slt-win'));

    const x = amount / bet;
    winbar.classList.remove('slt-hit'); void winbar.offsetWidth; winbar.classList.add('slt-hit');
    winbar.textContent = mult > 1
      ? t('win_mult', { raw: fmt(amount / mult), mult, amount: fmt(amount) })
      : t('win', { amount: fmt(amount), x: fmtX(x) });
    if (x >= 15) await bigWin(amount, x);
    else { sfx.win(x); hx.win(x); await sleep(T.win); }
  }

  // ---------- колесо фриспинов ----------
  // Сектора — config.wheel по порядку, дольки одинаковые (шансы сервер не отдаёт). Сектор выбран сервером
  // (bonus.wheel_sector), колесо только доезжает до него. Множители фриспинов — ×1, ×2 … ×N.
  function wheelSvg(W) {
    const R = 95, n = W.length, z = 360 / n;
    const pt = (a, r) => [(r * Math.sin(a * Math.PI / 180)).toFixed(2), (-r * Math.cos(a * Math.PI / 180)).toFixed(2)];
    let h = '<circle r="99" class="slt-rim"/>';
    W.forEach((w, i) => {
      const a0 = i * z, [x0, y0] = pt(a0, R), [x1, y1] = pt(a0 + z, R);
      const color = 'hsl(' + Math.round((340 + i * 280 / Math.max(1, n - 1)) % 360) + ',88%,64%)';
      h += '<path data-slice="' + i + '" fill="' + color + '" d="M0 0L' + x0 + ' ' + y0 + 'A' + R + ' ' + R + ' 0 ' + (z > 180 ? 1 : 0) + ' 1 ' + x1 + ' ' + y1 + 'Z"/>';
      const arc = z * Math.PI / 180 * 55, fn = Math.max(6, Math.min(20, arc * 0.6)), fm = Math.min(9.5, arc * 0.3);
      const spins = Number(w.spins) | 0;
      h += '<g transform="rotate(' + (a0 + z / 2) + ')"><text y="-45" class="slt-wn" style="font-size:' + fn.toFixed(1) + 'px">' + spins + '</text>'
        + (fm >= 5 ? '<text y="-72" class="slt-wm" style="font-size:' + fm.toFixed(1) + 'px">×1–' + spins + '</text>' : '') + '</g>';
    });
    h += '<circle r="97" class="slt-rim2"/>';
    for (let i = 0; i < 24; i++) { const [x, y] = pt(i * 15, 97); h += '<circle class="slt-bulb' + (i % 2 ? ' slt-odd' : '') + '" cx="' + x + '" cy="' + y + '" r="2.6"/>'; }
    return '<svg class="slt-wheel" viewBox="-100 -100 200 200">' + h + '</svg>';
  }

  function spinWheel(si, mults) {
    const ov = $.overlay, W = st.config.wheel, n = W.length, z = 360 / n;
    const wrap = el('div', 'slt-wheel-wrap');
    wrap.innerHTML = '<div class="slt-wheel-pointer"></div>' + wheelSvg(W) + '<div class="slt-wheel-hub">💎</div>';
    const btn = el('button', 'slt-wh-btn', t('wheel_spin'));
    btn.type = 'button';
    ov.replaceChildren(el('div', 'slt-wh-title', t('wheel_title')), wrap, btn);
    ov.classList.add('slt-wheel-mode');
    ov.classList.remove('slt-hidden');
    const svg = wrap.querySelector('.slt-wheel');

    const c = si * z + z * (0.5 + (Math.random() - 0.5) * 0.6); // не ровно по центру дольки
    const total = 360 * (st.turbo ? 4 : 6) + (360 - c);

    return new Promise(done => {
      let started = false;
      const start = () => {
        if (started) return;
        started = true;
        hx.tap('medium');
        const anim = svg.animate([{ transform: 'rotate(0deg)' }, { transform: 'rotate(' + total + 'deg)' }],
          { duration: st.turbo ? 2000 : 4600, easing: 'cubic-bezier(.12,.62,.08,1)', fill: 'forwards' });
        btn.style.visibility = 'hidden';
        ov.onclick = () => anim.finish();
        st.skip = () => anim.finish();
        // щелчок трещотки на каждой дольке под указателем
        let last = -1;
        (function tick() {
          const m = /matrix\(([^)]+)\)/.exec(getComputedStyle(svg).transform);
          if (m) {
            const [a, b] = m[1].split(',').map(Number);
            const rot = ((Math.atan2(b, a) * 180 / Math.PI) + 360) % 360;
            const sl = Math.floor(((360 - rot) % 360) / z);
            if (sl !== last) { if (last >= 0) { sfx.tick(); hx.tick(); } last = sl; }
          }
          if (anim.playState === 'running' && !st.dead) requestAnimationFrame(tick);
        })();
        anim.finished.then(async () => {
          st.skip = null; ov.onclick = null;
          const slice = svg.querySelector('[data-slice="' + si + '"]');
          if (slice) slice.classList.add('slt-won');
          sfx.big();
          hx.wheelWin();
          fx.burst(...fx.center(svg), 70, 12);
          const list = mults.slice(0, 3).map(x => '×' + x).join(', ') + (mults.length > 3 ? ' … ×' + mults[mults.length - 1] : '');
          btn.replaceWith(el('div', 'slt-wh-result', t('wheel_result', { n: mults.length, list })));
          await sleep(st.turbo ? 1000 : 2200);
          ov.classList.add('slt-hidden'); ov.classList.remove('slt-wheel-mode'); ov.replaceChildren();
          done();
        }, () => done());
      };
      btn.onclick = e => { e.stopPropagation(); start(); };
      ov.onclick = start;
      st.skip = start;
      if (st.auto) setTimeout(start, 700);
    });
  }

  // ---------- бонуска: вся пришла в slot:spin:ok, играется локально без запросов к серверу ----------
  async function runBonus(res) {
    const T = timing(), bonus = res.bonus, spins = bonus.spins, bet = res.bet;

    // праздник скаттеров
    clearWins();
    stage.classList.add('slt-has-win');
    (res.scatter ? res.scatter.cells : []).forEach(([r, row]) => {
      const c = reels[r].cells[row];
      c.classList.remove('slt-land'); c.classList.add('slt-party');
      fx.burst(...fx.center(c), 45, 10);
    });
    sfx.bonus();
    hx.bonus();
    winbar.classList.remove('slt-hit');
    winbar.textContent = t('bonus_trigger');
    await sleep(st.turbo ? 900 : 1800);

    fx.rain(90);
    const counts = st.config.wheel.map(w => w.spins);
    winbar.textContent = t('wheel_hint', { min: Math.min(...counts), max: Math.max(...counts) });
    await spinWheel(bonus.wheel_sector, spins.map(s => s.mult));
    if (st.dead) return;

    clearWins();
    root.classList.add('slt-bonus');
    $.ladder.replaceChildren(...spins.map(s => el('i', null, '×' + s.mult)));
    $.fsAll.textContent = spins.length; $.fsTotal.textContent = '0';
    $.fsbar.classList.remove('slt-hidden');

    let acc = 0;
    for (let i = 0; i < spins.length; i++) {
      if (st.dead) return;
      const s = spins[i];
      $.fsNow.textContent = i + 1;
      [...$.ladder.children].forEach((node, k) => { node.className = k < i ? 'slt-past' : k === i ? 'slt-on' : ''; });
      sfx.step(i);
      hx.step();
      winbar.classList.remove('slt-hit');
      winbar.textContent = t('fs_step', { i: i + 1, mult: s.mult });
      clearWins();
      await spinReels(s.grid, 'fs');
      if (s.amount > 0) {
        await showWins(s.wins, null, s.mult, bet, s.amount);
        const from = acc; acc += s.amount;
        reveal(s.amount);
        countUp($.fsTotal, from, acc, 350);
      } else {
        await sleep(T.pause);
      }
    }

    clearWins();
    const credit = bonus.total, x = credit / bet;
    const capped = st.config.max_win_x && res.total_win >= st.config.max_win_x * bet;
    sfx.big();
    hx.big(x >= 100 ? 3 : x >= 30 ? 2 : 1, st.turbo ? 1000 : 2400);
    fx.rain(x >= 100 ? 260 : x >= 30 ? 160 : 70);
    fx.burst(...fx.center(stage), 70, 14);
    const num = el('span', null, '0');
    const amt = el('div', 'slt-ov-amount'); amt.append(num, ' 💎');
    const sub = t('x_of_bet', { x: fmtX(x) }) + (capped ? ' · ' + t('max_win_reached') : '');
    const p = overlay([el('div', 'slt-ov-title', x >= 100 ? t('bonus_huge_title') : t('bonus_total_title')), amt, el('div', 'slt-ov-sub', sub)], st.turbo ? 2200 : 4500);
    countUp(num, 0, credit, st.turbo ? 1000 : 2400);
    await p;
    hx.stop();

    root.classList.remove('slt-bonus');
    $.fsbar.classList.add('slt-hidden');
    winbar.classList.add('slt-hit');
    winbar.textContent = t('bonus_result', { amount: fmt(credit), x: fmtX(x) });
  }

  // ---------- протокол: открытие сессии и спин ----------
  async function openSession(bpl, lines) {
    const cid = newId();
    socket.emit('slot:open', { bet_per_line: bpl, lines, client_id: cid });
    const isOpen = (type, d) => type === 'state' && d && d.session && !d.session.closing;
    let ev = await waitEvent((type, d) => isOpen(type, d) || (type === 'alert' && d.client_id === cid), OPEN_TIMEOUT_MS);
    // ответ об открытии потерялся, сессия могла открыться — ждём slot:state
    if (ev && ev.type === 'alert' && ev.data.code === 'open_unconfirmed') ev = await waitEvent(isOpen, 4000);
    if (st.dead) return false;
    if (!ev) { notice(t('err_timeout')); return false; }
    if (ev.type === 'alert') { handleAlert(ev.data); return false; }
    return true;
  }

  // Возвращает slot:spin:ok или null (причина уже показана на плашке)
  async function requestSpin(bpl, lines) {
    for (let attempt = 0; attempt < 2; attempt++) {
      if (!st.session && !(await openSession(bpl, lines))) return null;
      const wait = st.lastSpinAt + st.minSpinMs - Date.now();
      if (wait > 0) await sleep(wait);
      if (st.dead) return null;

      const cid = newId();   // новый на каждое нажатие; повтор с тем же id сервер не сыграет второй раз
      st.pendingCid = cid; st.pendingBet = bpl * lines; st.lastSpinAt = Date.now();
      scheduleBalance();
      socket.emit('slot:spin', { bet_per_line: bpl, lines, client_id: cid });
      const ev = await waitEvent((type, d) => (type === 'spin' || type === 'alert') && d && d.client_id === cid, SPIN_TIMEOUT_MS);
      st.pendingCid = null; st.pendingBet = 0; scheduleBalance();
      if (st.dead) return null;
      if (!ev) { notice(t('err_timeout')); return null; }
      if (ev.type === 'spin') return ev.data;

      const code = ev.data.code;
      // сессия закрылась по простою или после перезапуска — открываем заново и повторяем один раз
      if (attempt === 0 && (code === 'no_session' || code === 'session_closed')) { st.session = null; continue; }
      if (attempt === 0 && code === 'session_closing') {
        await waitEvent((type, d) => type === 'state' && d && !d.session, OPEN_TIMEOUT_MS);
        st.session = null;
        continue;
      }
      handleAlert(ev.data);
      return null;
    }
    return null;
  }

  // Почему сейчас нельзя крутить (ключ текста) или null
  function blocker() {
    if (!st.connected) return 'err_offline';
    if (!st.config || !reels.length) return 'loading';
    if (!st.ready) return 'err_not_ready';
    if (st.disabled && !st.session) return 'err_disabled';
    if (!st.bpl) return 'err_disabled';
    return null;
  }

  async function spin() {
    if (st.dead) return;
    if (st.busy) { if (st.skip) st.skip(); return; }
    const b = blocker();
    if (b) {
      const info = b === 'loading' || b === 'err_not_ready';
      notice(t(b), info ? 'info' : '');
      if (info) hx.warn(); else hx.error();
      st.auto = false; render(); return;
    }
    const bpl = st.bpl, lines = st.lines, bet = bpl * lines;
    if (st.main !== null && st.main + st.wallet < bet) {
      notice(t('err_no_funds'));
      hx.error();
      st.auto = false; render();
      if (typeof opts.onNoFunds === 'function') opts.onNoFunds();
      return;
    }

    st.busy = true;
    if (noticeTag !== 'offline') hideNotice();
    clearWins();
    winbar.classList.remove('slt-hit');
    winbar.innerHTML = '&nbsp;';
    render();
    sfx.spin();
    preSpin();

    const res = await requestSpin(bpl, lines);
    if (st.dead) return;
    if (!res) {
      cancelPreSpin();
      st.busy = false; st.auto = false;
      winbar.textContent = t('good_luck');
      render();
      return;
    }

    $.spinid.textContent = t('spin_id', { id: res.id });
    await spinReels(res.grid, 'base');
    if (st.dead) return;
    if (res.base_win > 0) {
      await showWins(res.wins, res.scatter, 1, res.bet, res.base_win);
      reveal(res.base_win);
    }
    if (res.bonus) await runBonus(res);
    if (st.dead) return;

    st.unrevealed = 0;
    st.busy = false;
    render();
    if (st.auto) setTimeout(() => { if (st.auto && !st.busy && !st.dead) spin(); }, st.turbo ? 110 : 380);
  }

  // ---------- коды user:alert (таблица в frontend_slots.md) ----------
  function handleAlert(d) {
    const code = d.code;
    st.auto = false;
    // Информационные коды — предупреждение, остальные — ошибка
    if (['not_ready', 'server_restart', 'too_fast', 'too_many_requests'].includes(code)) hx.warn(); else hx.error();
    switch (code) {
      case 'unauthorized':
        notice(t('err_login'));
        if (typeof opts.onLoginRequired === 'function') opts.onLoginRequired();
        break;
      case 'deposit_required':
        notice(t('err_deposit'));
        if (typeof opts.onDepositRequired === 'function') opts.onDepositRequired();
        break;
      case 'no_funds':
        notice(t('err_no_funds'));
        if (typeof opts.onNoFunds === 'function') opts.onNoFunds();
        break;
      case 'mode_disabled':
        st.disabled = true;
        notice(t('err_disabled'));
        break;
      case 'not_ready': notice(t('err_not_ready'), 'info'); break;
      case 'server_restart': notice(t('err_restart'), 'info'); break;
      case 'too_fast':
      case 'too_many_requests': notice(t('err_too_fast'), 'info'); break;
      case 'bet_out_of_range':
        if (st.config && d.min != null && d.max != null) { st.config.bet = { min: d.min, max: d.max }; clampBet(); }
        notice(t('err_bet_range', { min: fmt(d.min || 0), max: fmt(d.max || 0) }));
        break;
      case 'session_failed': notice(t('err_session_failed')); break;
      case 'server_error':
      case 'no_session':
      case 'session_closed':
      case 'session_closing': notice(t('err_server')); break;
      case 'invalid_input':
        console.warn('[slot] invalid_input: кривые bet_per_line / lines', d);
        notice(t('err_open'));
        break;
      default: notice(t('err_open')); // open_failed, bet_failed, streamer_restricted, user_not_found, bad_request
    }
    if (typeof opts.onAlert === 'function') opts.onAlert(d);
    render();
  }

  // ---------- ставка и линии ----------
  function steps() { return st.config ? betSteps(st.config.bet, st.lines) : []; }
  // Прижать линии и ставку к лимитам из slot:config. Возвращает true, если ставка изменилась.
  function clampBet() {
    const L = st.config.lines || { min: 1, max: 20 };
    const before = st.bpl * st.lines;
    if (!st.lines) st.lines = L.max;
    st.lines = Math.min(L.max, Math.max(L.min, st.lines));
    const s = steps();
    if (!s.length) { st.bpl = null; return before !== 0; }
    if (!st.bpl || st.bpl < s[0]) st.bpl = s[0];
    else if (!s.includes(st.bpl)) st.bpl = s.filter(v => v <= st.bpl).pop() || s[0];
    return st.bpl * st.lines !== before;
  }
  function step(act) {
    if (st.busy || !st.config) return;
    const before = st.bpl + ':' + st.lines;
    const s = steps(), i = s.indexOf(st.bpl), L = st.config.lines || { min: 1, max: 20 };
    if (act === 'bet-' && i > 0) st.bpl = s[i - 1];
    if (act === 'bet+' && i >= 0 && i < s.length - 1) st.bpl = s[i + 1];
    if (act === 'lines-') st.lines = Math.max(L.min, st.lines - 1);
    if (act === 'lines+') st.lines = Math.min(L.max, st.lines + 1);
    if (act === 'lines-' || act === 'lines+') clampBet();
    // Значение сменилось — щелчок выбора, упёрлись в предел — предупреждение
    if (before === st.bpl + ':' + st.lines) hx.edge(); else hx.select();
    savePrefs();
    render();
    if (act === 'lines-' || act === 'lines+') previewLines(st.lines - 1);
  }

  // ---------- отрисовка панели ----------
  function render() {
    if (st.dead) return;
    $.lineBet.textContent = st.bpl ? fmt(st.bpl) : '—';
    $.nLines.textContent = st.lines || '—';
    $.totalBet.textContent = st.bpl ? fmt(st.bpl * st.lines) : '—';
    const b = $.btnSpin;
    b.textContent = st.busy ? t('stop') : t('spin');
    b.classList.toggle('slt-busy', st.busy);
    b.disabled = !st.busy && (!st.connected || !st.config || !st.ready);
    $.controls.classList.toggle('slt-locked', st.busy);
    $.btnAuto.classList.toggle('slt-on', st.auto);
    $.btnTurbo.classList.toggle('slt-on', st.turbo);
    $.btnSound.textContent = st.sound ? '🔊' : '🔇';
    renderBalance();
  }

  // ---------- таблица выплат (из slot:config: суммы для текущей ставки на линию) ----------
  function showPaytable() {
    if (!st.config) return;
    const cfg = st.config, m = $.modal, bpl = st.bpl || 1;
    const idOf = {}; cfg.symbols.forEach(s => { idOf[s.key] = s.id; });
    const ico = id => { const d = el('div', 'slt-ico'); d.appendChild(cell(id).firstChild); return d; };
    const pays = lines => { const d = el('div', 'slt-pays'); lines.forEach((ln, i) => { if (i) d.appendChild(el('br')); d.append(...ln); }); return d; };
    const sheet = el('div', 'slt-sheet');
    const close = el('button', 'slt-close', t('pt_close')); close.type = 'button';
    sheet.append(close, el('h2', null, t('pt_title')), el('p', null, t('pt_intro', { bet: fmt(bpl) })));

    const pt = el('div', 'slt-pt');
    ['H4', 'H3', 'H2', 'H1', 'L4', 'L3', 'L2', 'L1'].forEach(key => {
      const id = idOf[key]; const p = cfg.paytable[String(id)];
      if (id == null || !Array.isArray(p)) return;
      const row = el('div');
      row.append(ico(id), pays([[t('sym_' + key)], ['5 — ', el('b', null, fmt(p[2] * bpl))], ['4 — ', el('b', null, fmt(p[1] * bpl))], ['3 — ', el('b', null, fmt(p[0] * bpl))]]));
      pt.appendChild(row);
    });
    sheet.appendChild(pt);

    const sc = (cfg.paytable[String(st.S)] || {}).total_bet_x || [2, 10, 50];
    const sp = el('div', 'slt-pt');
    const w = el('div'); w.append(ico(st.W), pays([[t('pt_wild')]]));
    const s = el('div'); s.append(ico(st.S), pays([[t('pt_scatter', { x3: sc[0], x4: sc[1], x5: sc[2] })]]));
    sp.append(w, s);
    sheet.append(el('h3', null, t('pt_special')), sp);

    sheet.append(el('h3', null, t('pt_bonus_title')), el('p', null, t('pt_bonus')));
    const wl = el('p');
    cfg.wheel.forEach((x, i) => { if (i) wl.appendChild(el('br')); wl.append(el('b', null, t('pt_wheel_row', { n: x.spins }))); });
    sheet.appendChild(wl);
    if (cfg.max_win_x) sheet.appendChild(el('p', null, t('pt_max', { x: fmt(cfg.max_win_x) })));

    sheet.appendChild(el('h3', null, t('pt_lines', { n: st.lines })));
    const grid = el('div', 'slt-lines-grid');
    cfg.paylines.forEach((ln, l) => {
      const box = el('div', 'slt-ln' + (l < st.lines ? ' slt-on' : ''));
      const mini = el('div', 'slt-mini');
      for (let row = 0; row < 3; row++) for (let r = 0; r < 5; r++) mini.appendChild(el('i', ln[r] === row ? 'slt-p' : null));
      box.append(mini, String(l + 1));
      grid.appendChild(box);
    });
    sheet.appendChild(grid);

    m.replaceChildren(sheet);
    m.classList.remove('slt-hidden');
    m.onclick = e => { if (e.target === m || e.target === close) m.classList.add('slt-hidden'); };
  }

  // ---------- события сервера ----------
  function onConfig(d) {
    const first = !st.config;
    st.config = d;
    st.keyOf = []; st.W = -1; st.S = -1;
    d.symbols.forEach(s => { st.keyOf[s.id] = s.key; if (s.wild) st.W = s.id; if (s.scatter) st.S = s.id; });
    buildPools();
    if (first) initReels();
    // смена лимитов в админке во время игры: прижать ставку и остановить автоигру
    if (clampBet() && !first) st.auto = false;
    if (first) winbar.textContent = t('good_luck');
    savePrefs();
    render();
  }

  function onState(d) {
    const prevWallet = st.wallet;
    st.ready = d.ready !== false;
    st.disabled = !!d.disabled;
    if (d.min_spin_ms != null) st.minSpinMs = d.min_spin_ms;
    if (d.session) {
      if (!st.session || st.session.id !== d.session.id) st.lastSeq = -1;
      st.session = d.session;
      st.wallet = d.session.wallet;
      if (d.session.seq > st.lastSeq) st.lastSeq = d.session.seq;
    } else {
      st.session = null;
      st.wallet = 0;
    }
    if (d.closed && d.closed.reason !== 'client') {
      const r = d.closed.reason, n = d.closed.returned || 0;
      const key = r === 'idle' ? 'closed_idle' : r === 'shutdown' ? 'closed_shutdown' : (!n && prevWallet > 0 ? 'closed_pending' : 'closed_other');
      notice(t(key, { amount: fmt(n) }), 'info');
      st.auto = false;
    }
    if (st.disabled && !st.session && !st.busy) notice(t('err_disabled'));
    render();
    feed('state', d);
  }

  function onSpin(d) {
    const id = String(d.id || ''), dash = id.lastIndexOf('-');
    const sid = id.slice(0, dash), seq = parseInt(id.slice(dash + 1), 10);
    if (d.client_id && d.client_id === st.pendingCid) { st.pendingBet = 0; st.unrevealed = d.total_win || 0; }
    // wallet — резерв после спина (с бонуской). Спины других вкладок тоже двигают резерв.
    if ((!st.session || st.session.id === sid) && !(seq <= st.lastSeq)) { st.wallet = d.wallet; st.lastSeq = seq; }
    scheduleBalance();
    feed('spin', d);
  }

  const handlers = {
    connect: () => { st.connected = true; hideNotice('offline'); socket.emit('subscribe', { game: 'slot' }); render(); },
    disconnect: () => { st.connected = false; st.auto = false; notice(t('err_offline'), 'info', 'offline'); render(); },
    'slot:config': d => { if (d && d.symbols) onConfig(d); },
    'slot:state': d => { if (d) onState(d); },
    'slot:spin:ok': d => { if (d) onSpin(d); },
    'slot:wallet': d => { if (d && st.session && d.session === st.session.id) { st.wallet = d.wallet; scheduleBalance(); } },
    'slot:disabled': d => {
      st.disabled = !!(d && d.disabled);
      if (st.disabled) { st.auto = false; notice(t(st.session ? 'notice_disabling' : 'err_disabled'), st.session ? 'info' : ''); }
      else hideNotice();
      render();
    },
    'user:balance': d => { if (d && typeof d.balance === 'number') { st.main = d.balance; scheduleBalance(); } },
    'user:alert': d => { if (!d || d.game !== 'slot') return; if (!feed('alert', d)) handleAlert(d); },
  };
  Object.keys(handlers).forEach(ev => socket.on(ev, handlers[ev]));
  if (socket.connected) socket.emit('subscribe', { game: 'slot' });
  else notice(t('err_offline'), 'info', 'offline');

  // ---------- кнопки ----------
  root.querySelectorAll('[data-act]').forEach(b => b.addEventListener('click', () => step(b.dataset.act)));
  $.btnSpin.addEventListener('click', () => { hx.tap(st.busy ? 'light' : 'medium'); spin(); });
  $.btnAuto.addEventListener('click', () => { st.auto = !st.auto; hx.tap(st.auto ? 'medium' : 'light'); render(); if (st.auto && !st.busy) spin(); });
  $.btnTurbo.addEventListener('click', () => { st.turbo = !st.turbo; hx.select(); savePrefs(); render(); });
  $.btnSound.addEventListener('click', () => {
    st.sound = !st.sound; hx.select(); render();
    if (typeof opts.onSoundChange === 'function') opts.onSoundChange(st.sound);
  });
  $.btnPay.addEventListener('click', () => { hx.tap(); showPaytable(); });
  const onKey = e => {
    if (e.code !== 'Space' || e.repeat) return;
    const tag = (e.target && e.target.tagName) || '';
    if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag) || (e.target && e.target.isContentEditable)) return;
    if (!root.isConnected || !root.offsetParent || !$.modal.classList.contains('slt-hidden')) return;
    e.preventDefault();
    spin();
  };
  addEventListener('keydown', onKey);
  // свернули мини-апп / ушли во вкладку — автоигру останавливаем
  const onVis = () => { if (document.hidden && st.auto) { st.auto = false; render(); } };
  document.addEventListener('visibilitychange', onVis);

  render();

  // ---------- наружу ----------
  function destroy() {
    if (st.dead) return;
    st.auto = false;
    try { if (st.skip) st.skip(); } catch (e) { /* уже закрыто */ }
    st.dead = true;
    // уход с экрана: сначала закрыть сессию (резерв вернётся на баланс), потом отписаться
    if (socket.connected) {
      socket.emit('slot:close');
      socket.emit('unsubscribe', { game: 'slot' });
    }
    Object.keys(handlers).forEach(ev => socket.off(ev, handlers[ev]));
    for (const w of waiters) { clearTimeout(w.timer); w.res(null); }
    waiters.clear();
    removeEventListener('keydown', onKey);
    document.removeEventListener('visibilitychange', onVis);
    clearTimeout(previewTimer);
    if (balRaf) cancelAnimationFrame(balRaf);
    if (ro) ro.disconnect();
    reels.forEach(R => { if (R.pre) R.pre.cancel(); R.strip.getAnimations().forEach(a => a.cancel()); });
    fx.destroy();
    sfx.destroy();
    hx.stop();
    root.innerHTML = '';
    root.classList.remove('slt', 'slt-bonus');
    root.style.removeProperty('--cell');
  }

  return {
    destroy,
    /** Включить/выключить звук снаружи (например, по флагу sound из /api/me/) */
    setSound(on) { st.sound = !!on; render(); },
    /** Остановить автоигру (например, при открытии модалки приложения поверх слота) */
    stopAuto() { st.auto = false; render(); },
    /** Текущий баланс на экране: основной, резерв и показанная сумма */
    getBalance() { return { main: st.main, wallet: st.wallet, shown: st.lastShown }; },
  };
}
